import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { AuthService } from '@app/core/services/auth.service';
import { MenuConfig, MenuConfigPayload, MenuItem, MenuSection } from '@app/core/models/menu.model';
import { SettingsService, SettingType } from '@app/core/services/settings.service';
import { LanguageService } from '@app/core/services/language.service';
import { CacheService } from '@app/core/services/cache.service';
import { Constants } from './constants';

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  private authService = inject(AuthService);
  private router = inject(Router);
  private settingsService = inject(SettingsService);
  private languageService = inject(LanguageService);
  private cacheService = inject(CacheService);

  /**
   * Default menu configuration (fallback when database is unavailable)
   */
  private defaultMenuConfig: MenuConfig = {
    sections: [
      {
        id: 'main',
        labels: {
          'en-US': 'Main',
          'fr-FR': 'Principal'
        },
        order: 1,
        items: [
          {
            id: `/${Constants.mainLandingPage}`,
            labels: {
              'en-US': 'Home',
              'fr-FR': 'Accueil'
            },
            icon: 'home-outline',
            route: `/${Constants.mainLandingPage}`,
          },
          {
            id: 'dashboard',
            labels: {
              'en-US': 'Dashboard',
              'fr-FR': 'Tableau de bord'
            },
            icon: 'analytics-outline',
            route: '/home2',
            roles: ['CLIENT_ADMIN', 'MANAGER'],
          },
        ]
      },
      {
        id: 'management',
        labels: {
          'en-US': 'Management',
          'fr-FR': 'Gestion'
        },
        order: 2,
        roles: ['CLIENT_ADMIN', 'MANAGER'],
        items: [
          {
            id: 'clients',
            labels: {
              'en-US': 'Clients',
              'fr-FR': 'Clients'
            },
            icon: 'business-outline',
            route: '/clients',
            roles: ['CLIENT_ADMIN'],
          },
          {
            id: 'subsidiaries',
            labels: {
              'en-US': 'Subsidiaries',
              'fr-FR': 'Filiales'
            },
            icon: 'git-network-outline',
            route: '/subsidiaries',
            roles: ['CLIENT_ADMIN', 'MANAGER'],
          },
          {
            id: 'users',
            labels: {
              'en-US': 'Users',
              'fr-FR': 'Utilisateurs'
            },
            icon: 'people-outline',
            route: '/users',
            roles: ['CLIENT_ADMIN', 'MANAGER'],
          },
        ]
      },
      {
        id: 'insurance',
        labels: {
          'en-US': 'Insurance',
          'fr-FR': 'Assurance'
        },
        order: 3,
        items: [
          {
            id: 'policies',
            labels: {
              'en-US': 'Policies',
              'fr-FR': 'Polices'
            },
            icon: 'document-text-outline',
            route: '/policies',
          },
          {
            id: 'properties',
            labels: {
              'en-US': 'Properties',
              'fr-FR': 'Biens'
            },
            icon: 'location-outline',
            route: '/properties',
          },
          {
            id: 'claims',
            labels: {
              'en-US': 'Claims',
              'fr-FR': 'Sinistres'
            },
            icon: 'alert-circle-outline',
            route: '/claims',
          },
        ]
      },
      {
        id: 'account',
        labels: {
          'en-US': 'Account',
          'fr-FR': 'Mon compte'
        },
        order: 4,
        items: [
          {
            id: 'profile',
            labels: {
              'en-US': 'Profile',
              'fr-FR': 'Profil'
            },
            icon: 'person-outline',
            route: '/profile',
          },
          {
            id: 'settings',
            labels: {
              'en-US': 'Settings',
              'fr-FR': 'Paramètres'
            },
            icon: 'settings-outline',
            route: '/settings',
          },
          {
            id: 'document-tree-picker',
            labels: {
              'en-US': 'Document Tree',
              'fr-FR': 'Arbre documentaire'
            },
            icon: 'git-branch-outline',
            route: '/document-tree-picker',
            roles: ['SUPER_ADMIN', 'CLIENT_ADMIN', 'SUBSIDIARY_ADMIN'],
          },
        ]
      }
    ],
    collapseOnMobile: true,
    defaultCollapsed: false
  };

  private isCollapsedSubject = new BehaviorSubject<boolean>(false);
  public isCollapsed$ = this.isCollapsedSubject.asObservable();

  constructor() {}

  /**
   * Load menu configuration from database with fallback to default
   * Uses hierarchical resolution based on user's client and subsidiary
   * Caching is handled by SettingsService through CacheService
   */
  private loadMenuConfig(clientId?: string, subsidiaryId?: string): Observable<MenuConfig> {
    // Fetch from database with hierarchical parameters
    // SettingsService handles caching through CacheService
    // Use merged settings (unmerged: false) to get the hierarchical configuration
    return this.settingsService
      .getSettingByKey('menu_configuration', clientId || '*', subsidiaryId || '*', false)
      .pipe(
        map(menuConfig => {
          // getSettingByKey can return T | T[], but menu_configuration should be a single value
          const config = Array.isArray(menuConfig) ? menuConfig[0] : menuConfig;
          
          // If config is null/undefined or has no sections, use default
          if (!config || !config.value?.sections || config.value.sections.length === 0) {
            console.warn('Empty menu configuration received, using defaults');
            this.loadMenuTranslations(this.defaultMenuConfig);
            return this.defaultMenuConfig;
          }
          
          // Load translations synchronously before returning the config
          this.loadMenuTranslations(config.value);
          return config.value;
        }),
        catchError(error => {
          // Check if this is a backend error (server error or network issue)
          const status = error?.status || error?.error?.status;
          const isBackendError = !status || status >= 500 || status === 0;
          
          if (true || isBackendError) {
            // // Backend is down or unreachable - DO NOT route to error page for menu issues
            // // Just use default menu configuration to maintain app functionality
            // console.error('Backend error while loading menu configuration, using defaults:', error);
            // this.loadMenuTranslations(this.defaultMenuConfig);
            // return of(this.defaultMenuConfig);
            
            // Route to error page with message and return empty menu to prevent app from breaking
            console.error('Backend error while loading menu configuration, routing to error page:', error);
            this.router.navigate(['/error'], { 
              queryParams: { 
                message: 'Unable to load menu configuration. Please try again later.', 
                returnUrl: `/${Constants.mainLandingPage}`,
                technicalDetails: error?.message || 'No additional details available'
              } 
            });
            return of({ sections: [] }); // Return empty menu to prevent app from breaking
          }
          
          // 404 or other client errors - use default menu configuration
          console.warn('Menu configuration not found in database (status: ' + status + '), using defaults:', error);
          this.loadMenuTranslations(this.defaultMenuConfig);
          return of(this.defaultMenuConfig);
        })
      );
  }

  /**
   * Load dynamic translations from menu configuration
   * Extracts labels from sections and items and adds them to the language service
   * Translation keys follow pattern: menu_configuration.ui_configuration.{sectionId} or
   * menu_configuration.ui_configuration.{sectionId}.{itemId}
   */
  private loadMenuTranslations(menuConfig: MenuConfig): void {
    if (!menuConfig || !menuConfig.sections) {
      return;
    }

    try {
      const settingKey = 'menu_configuration';
      const settingType = 'ui_configuration';
      const translations: Record<string, Record<string, string>> = {};

      // Extract translations from sections and items
      menuConfig.sections.forEach(section => {
        const sectionKey = `${settingKey}.${settingType}.${section.id}`;
        
        // Add section labels
        if (section.labels) {
          Object.entries(section.labels).forEach(([lang, label]) => {
            if (!translations[lang]) {
              translations[lang] = {};
            }
            translations[lang][sectionKey] = label;
          });
        }

        // Add item labels
        section.items.forEach(item => {
          const itemKey = `${settingKey}.${settingType}.${section.id}.${item.id}`;
          
          if (item.labels) {
            Object.entries(item.labels).forEach(([lang, label]) => {
              if (!translations[lang]) {
                translations[lang] = {};
              }
              translations[lang][itemKey] = label;
            });
          }

          // Recursively handle children
          if (item.children) {
            this.extractChildrenLabels(item.children, `${settingKey}.${settingType}.${section.id}.${item.id}`, translations);
          }
        });
      });

      // Add all extracted translations to language service
      this.languageService.addTranslationsForAllLanguages(translations, true);
    } catch (error) {
      console.warn('Failed to load menu translations:', error);
    }
  }

  /**
   * Recursively extract labels from child menu items
   */
  private extractChildrenLabels(
    children: MenuItem[],
    parentKey: string,
    translations: Record<string, Record<string, string>>
  ): void {
    children.forEach(child => {
      const childKey = `${parentKey}.${child.id}`;
      
      if (child.labels) {
        Object.entries(child.labels).forEach(([lang, label]) => {
          if (!translations[lang]) {
            translations[lang] = {};
          }
          translations[lang][childKey] = label;
        });
      }

      if (child.children) {
        this.extractChildrenLabels(child.children, childKey, translations);
      }
    });
  }

  /**
   * Get menu configuration filtered by user roles
   * Automatically loads the appropriate menu based on user's client/subsidiary
   */
  getMenu(): Observable<MenuConfig> {
    // Get current user to determine which menu configuration to load
    const user = this.authService.currentUserValue;
    if (!user) {
      return of({ sections: [] });
    }

    // Use the user's clientId and the first subsidiaryId from role assignments
    // for hierarchical menu loading
    const clientId = user.clientId;
    const subsidiaryId = user.roleAssignments.length > 0 
      ? user.roleAssignments[0].subsidiaryId 
      : undefined;

    return this.loadMenuConfig(clientId, subsidiaryId).pipe(
      map(menuConfig => {
        const userRoles = user.roleAssignments.map(ra => ra.roleId);
        const settingKey = 'menu_configuration';
        const settingType = 'ui_configuration';
        
        // Filter sections and items based on user roles and add translation keys
        const filteredSections = menuConfig.sections
          .filter((section: MenuSection) => this.hasAccess(section.roles, userRoles))
          .map((section: MenuSection) => ({
            ...section,
            labelKey: `${settingKey}.${settingType}.${section.id}`, // Add translation key for section
            items: section.items
              .filter((item: MenuItem) => this.hasAccess(item.roles, userRoles))
              .map((item: MenuItem) => this.addTranslationKeys(item, `${settingKey}.${settingType}.${section.id}`, userRoles))
          }))
          .filter((section: MenuSection) => section.items.length > 0)
          .sort((a: MenuSection, b: MenuSection) => a.order - b.order);

        return {
          ...menuConfig,
          sections: filteredSections
        };
      })
    );
  }

  /**
   * Add translation keys to menu item and filter children based on roles
   */
  private addTranslationKeys(item: MenuItem, parentKey: string, userRoles: string[]): MenuItem {
    const itemKey = `${parentKey}.${item.id}`;
    const filteredItem: MenuItem = {
      ...item,
      labelKey: itemKey // Add translation key
    };
    
    if (item.children) {
      filteredItem.children = item.children
        .filter(child => this.hasAccess(child.roles, userRoles))
        .map(child => this.addTranslationKeys(child, itemKey, userRoles));
    }
    
    return filteredItem;
  }

  /**
   * Extract clientId from subsidiaryId
   * This method is kept for potential future use but is no longer needed
   * since User model now has clientId directly
   * @deprecated Use user.clientId instead
   */
  private extractClientId(subsidiaryId: string | undefined): string | undefined {
    if (!subsidiaryId || subsidiaryId === '*') {
      return undefined;
    }
    // This is now handled by the User model's clientId field
    return undefined;
  }

  /**
   * Check if user has access based on required roles
   */
  private hasAccess(requiredRoles: string[] | undefined, userRoles: string[]): boolean {
    if (!requiredRoles || requiredRoles.length === 0) {
      return true; // No role requirement
    }
    
    return requiredRoles.some(role => userRoles.includes(role));
  }

  /**
   * Toggle menu collapsed state
   */
  toggleCollapse(): void {
    this.isCollapsedSubject.next(!this.isCollapsedSubject.value);
  }

  /**
   * Set collapsed state
   */
  setCollapsed(collapsed: boolean): void {
    this.isCollapsedSubject.next(collapsed);
  }

  /**
   * Navigate to menu item
   */
  navigateTo(item: MenuItem): void {
    if (item.action) {
      item.action();
    } else if (item.route) {
      this.router.navigate([item.route]);
    }
  }

  /**
   * Add custom menu item dynamically
   * Note: This modifies the default config only, not the database
   */
  addMenuItem(sectionId: string, item: MenuItem): void {
    const section = this.defaultMenuConfig.sections.find((s: MenuSection) => s.id === sectionId);
    if (section) {
      section.items.push(item);
      // No need to invalidate cache since we're modifying the default config
      // which is used as a fallback when database load fails
    }
  }

  /**
   * Update menu badge
   * Note: This modifies the default config only, not the database
   */
  updateBadge(itemId: string, badge: { text: string; color?: string } | undefined): void {
    for (const section of this.defaultMenuConfig.sections) {
      const item = section.items.find((i: MenuItem) => i.id === itemId);
      if (item) {
        item.badge = badge;
        break;
      }
    }
  }

  /**
   * Reload menu configuration from database
   * Call this after updating menu settings or when user context changes
   * Invalidates the cache through CacheService
   */
  reloadMenuConfig(): void {
    // Invalidate all menu_configuration caches
    // This will clear all variations (different clientId/subsidiaryId combinations)
    this.cacheService.invalidate('settings', 'menu_configuration');
  }
}
